import { File01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  dashboardId: string;
}

const TEMPLATES: ReportTemplate[] = [
  {
    id: 'oee',
    name: 'OEE Report',
    description:
      'Overall Equipment Effectiveness — Availability × Performance × Quality with industry benchmarks',
    category: 'Reliability',
    dashboardId: 'oee-report',
  },
  {
    id: 'downtime',
    name: 'Downtime Analysis',
    description:
      'Unplanned downtime by asset, cause, and time — identify worst offenders and recurring patterns',
    category: 'Reliability',
    dashboardId: 'downtime-analysis',
  },
  {
    id: 'pm-compliance',
    name: 'PM Compliance',
    description:
      'Preventive maintenance schedule adherence and overdue tracking with industry target ≥ 90%',
    category: 'Maintenance',
    dashboardId: 'pm-compliance',
  },
  {
    id: 'cost',
    name: 'Cost Analysis',
    description:
      'Maintenance cost breakdown — labor, parts, and overhead by asset, category, and time',
    category: 'Financial',
    dashboardId: 'cost-analysis',
  },
  {
    id: 'production',
    name: 'Production Overview',
    description: 'Work orders, completion rates, and operational metrics across all assets',
    category: 'Operations',
    dashboardId: 'production-overview',
  },
  {
    id: 'inventory',
    name: 'Inventory Overview',
    description: 'Stock levels, inventory value, and warehouse metrics with turnover analysis',
    category: 'Operations',
    dashboardId: 'inventory-overview',
  },
];

export const Route = createFileRoute('/_authenticated/reports/templates')({
  component: ReportTemplatesPage,
});

import { getDashboardUrl } from '@/lib/dashboard-theme';

function ReportTemplatesPage() {
  const { t } = useTranslation();

  const categories = [...new Set(TEMPLATES.map((t) => t.category))];

  const handleOpen = (template: ReportTemplate) => {
    const url = getDashboardUrl({ dashboard: template.dashboardId, showSidebar: 'false' });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <HugeiconsIcon icon={File01Icon} className="size-6" />
          {t('sidebar.reports.templates', 'Report Templates')}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t(
            'reports.templates.description',
            'Pre-built report templates for common CMMS analytics — OEE, downtime, PM compliance, and cost',
          )}
        </p>
      </div>

      {categories.map((category) => (
        <div key={category}>
          <h2 className="text-lg font-semibold mb-3 text-muted-foreground">{category}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATES.filter((t) => t.category === category).map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                onClick={() => handleOpen(template)}
              >
                <CardHeader>
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  <CardDescription className="text-xs">{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <span className="text-xs text-primary font-medium">Open Dashboard →</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
